import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { FdiChart } from "@/components/common/FdiChart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Mic, Square, Pause, Play, Loader2, Sparkles, Trash2, Plus, ArrowRight,
  Upload, ImageIcon, AlertTriangle, Check, Pencil,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ImagingReview } from "@/components/common/ImagingReview";
import { toast } from "sonner";

const STATUSES = ["Missing", "Root stump", "Mobile", "Grossly carious"];

export default function VoiceConsultation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const rec = useVoiceRecorder();
  const [c, setC] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [structuring, setStructuring] = useState(false);
  const [imaging, setImaging] = useState([]);
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef(null);
  const pendingType = useRef("Image");
  const cRef = useRef(null);
  cRef.current = c;

  const load = () => api.get("/consultations").then((r) => {
    const found = r.data.find((x) => x.id === id);
    setC(found || null);
    setTranscript(found?.transcript || "");
  });
  useEffect(() => { load(); api.get(`/imaging?consultation_id=${id}`).then((r) => setImaging(r.data)); }, [id]);
  const [formQuestions, setFormQuestions] = useState([]);
  useEffect(() => {
    if (!c?.condition) return;
    api.get(`/forms/effective?condition=${c.condition}`).then((r) => {
      const qs = (r.data?.questions || []).filter((q) => (c.tier || "quick") === "comprehensive" || q.tier !== "comprehensive");
      setFormQuestions(qs);
    }).catch(() => setFormQuestions([]));
  }, [c?.condition, c?.tier]);
  const needsPoll = imaging.some((im) => !im.ai_assessment && ["analyzing", undefined, null].includes(im.assessment_status));
  useEffect(() => {
    if (!needsPoll) return;
    const iv = setInterval(() => api.get(`/imaging?consultation_id=${id}`).then((r) => setImaging(r.data)).catch(() => {}), 3000);
    return () => clearInterval(iv);
  }, [needsPoll, id]);
  const pendingFindings = imaging.reduce((n, im) => n + (im.review_items || []).filter((x) => x.status === "pending").length, 0);

  if (!c) return <Skeleton className="h-96 rounded-2xl" />;

  const save = async (patch) => {
    const next = { ...cRef.current, ...patch };
    cRef.current = next;
    setC(next);
    await api.put(`/consultations/${id}`, next);
  };

  const runStructuring = async (text) => {
    setStructuring(true);
    try {
      const existing = { patient: { name: c.patient_name }, chief_complaint: c.chief_complaint, tooth_findings: c.tooth_findings, sites: c.sites, arch_status: c.arch_status, soft_tissue: c.soft_tissue, form_answers: c.form_answers };
      const { data } = await api.post("/ai/structure", { transcript: text, existing, condition: c.condition_name || c.condition, form_questions: formQuestions });
      const s = data.structured || {};
      const patch = {
        transcript: (c.transcript ? c.transcript + " " : "") + text,
        chief_complaint: s.chief_complaint || c.chief_complaint,
        medical_history: s.medical_history?.length ? s.medical_history : c.medical_history,
        dental_history: s.dental_history?.length ? s.dental_history : c.dental_history,
        arch_status: s.arch_status || c.arch_status,
        arch_side: s.arch_side || c.arch_side,
        soft_tissue: s.soft_tissue || c.soft_tissue,
        tooth_findings: mergeByKey(c.tooth_findings, s.tooth_findings, "fdi"),
        sites: mergeByKey(c.sites, s.sites, "site"),
        risk_factors: s.risk_factors?.length ? s.risk_factors : c.risk_factors,
        patient_expectations: s.patient_expectations || c.patient_expectations,
        patient_name: (c.patient_id ? c.patient_name : (s.patient?.name || c.patient_name)),
        extracted_patient: s.patient || c.extracted_patient,
        form_answers: { ...(c.form_answers || {}), ...(s.form_answers || {}) },
      };
      await save(patch);
      // create/update patient if new
      if (!c.patient_id && s.patient?.name) {
        const { data: p } = await api.post("/patients", { name: s.patient.name, age: s.patient.age, gender: s.patient.gender, phone: s.patient.phone, email: s.patient.email, chief_complaint: s.chief_complaint });
        await save({ patient_id: p.id, patient_name: p.name });
      } else if (c.patient_id && s.patient && (s.patient.age || s.patient.gender || s.patient.phone)) {
        api.put(`/patients/${c.patient_id}`, Object.fromEntries(Object.entries({ age: s.patient.age, gender: s.patient.gender, phone: s.patient.phone, email: s.patient.email }).filter(([, v]) => v))).catch(() => {});
      }
      const n = Object.keys(s.form_answers || {}).length;
      toast.success(n ? `Consultation structured · ${n} form field${n === 1 ? "" : "s"} filled from voice` : "Consultation structured");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Structuring failed");
    } finally { setStructuring(false); }
  };

  const handleStop = async () => {
    const result = await rec.stop();
    if (result && result.error) return toast.error(result.error);
    if (result) { setTranscript((t) => (t ? t + " " : "") + result); await runStructuring(result); }
  };

  const uploadImaging = async (file, type) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("consultation_id", id); fd.append("image_type", type);
    try {
      const { data } = await api.post("/imaging/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImaging((prev) => [data, ...prev]);
      if (!data.quality_ok) toast.warning(data.quality_note);
      else toast.success(`${type} uploaded`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  const pickType = (t) => { pendingType.current = t; fileRef.current?.click(); };

  const toggleTooth = (fdi) => {
    const list = c.tooth_findings || [];
    const idx = list.findIndex((f) => String(f.fdi) === String(fdi));
    if (idx === -1) save({ tooth_findings: [...list, { fdi, status: "Missing", confidence: 100 }] });
    else {
      const cur = list[idx];
      const next = STATUSES.indexOf(cur.status) + 1;
      if (next >= STATUSES.length) save({ tooth_findings: list.filter((_, i) => i !== idx) });
      else save({ tooth_findings: list.map((f, i) => i === idx ? { ...f, status: STATUSES[next] } : f) });
    }
  };

  const updateSite = (i, key, val) => save({ sites: (c.sites || []).map((s, x) => x === i ? { ...s, [key]: val } : s) });
  const addSite = () => save({ sites: [...(c.sites || []), { site: "", status: "Missing", md: "", bl: "", vertical: "", tissue: "" }] });
  const removeSite = (i) => save({ sites: (c.sites || []).filter((_, x) => x !== i) });

  const generatePlan = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/treatment-plan", { consultation_id: id });
      toast.success("Treatment plan drafted — doctor review required");
      navigate(`/treatment-plan/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const micLabel = { idle: "Start Recording", recording: "Recording", paused: "Paused", transcribing: "Understanding your consultation…" }[rec.state];
  const hasData = (c.tooth_findings?.length || c.sites?.length || c.chief_complaint || c.arch_status || Object.keys(c.form_answers || {}).length);
  const isImplantCase = !c.condition || c.condition === "implant";

  return (
    <div>
      <PageHeader breadcrumb={["Workspace", "Consultation"]} title={`${c.tier === "comprehensive" ? "Comprehensive" : "Quick"} ${c.condition_name || "Dental"} Consultation`}
        subtitle={`${c.patient_name || "New patient"} · ${c.id.slice(0, 8).toUpperCase()} · ${new Date(c.created_at).toLocaleString()}`}
        actions={<StatusBadge status={c.status === "in_progress" ? "in_progress" : c.status} />}
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Voice + imaging column */}
        <div className="lg:col-span-2 space-y-6">
          <SurfaceCard dark className="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#8B9698] mb-4">Voice Consultation</div>
              <button
                onClick={rec.state === "idle" ? rec.start : handleStop}
                disabled={rec.state === "transcribing" || structuring}
                data-testid="mic-button"
                className={cn(
                  "relative flex h-28 w-28 items-center justify-center rounded-full transition-all",
                  rec.state === "recording" ? "bg-red-500 shadow-[0_0_0_10px_rgba(239,68,68,0.15)] animate-pulse" : "bg-[#00A85A] shadow-[0_8px_30px_rgba(0,168,90,0.4)] hover:bg-[#008F4D]"
                )}
              >
                {rec.state === "transcribing" || structuring ? <Loader2 className="h-10 w-10 text-white animate-spin" /> :
                 rec.state === "recording" ? <Square className="h-9 w-9 text-white" fill="white" /> : <Mic className="h-11 w-11 text-white" />}
              </button>
              <div className="mt-4 text-sm font-bold text-white">
                {structuring ? "Structuring clinical findings…" : micLabel}
                {rec.state === "recording" && <span className="ml-2 tabular-nums">{String(Math.floor(rec.seconds / 60)).padStart(2, "0")}:{String(rec.seconds % 60).padStart(2, "0")}</span>}
              </div>
              <div className="mt-1 text-xs text-[#8B9698]">Speak naturally. You don't need to follow a script.</div>
              {rec.state === "recording" && (
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={rec.pause} className="rounded-lg border-[#223139] bg-transparent text-white hover:bg-[#0E1A21]"><Pause className="h-3.5 w-3.5 mr-1" />Pause</Button>
                </div>
              )}
              {rec.state === "paused" && (
                <div className="mt-4 flex gap-2">
                  <Button size="sm" onClick={rec.resume} className="rounded-lg bg-[#00A85A] text-white"><Play className="h-3.5 w-3.5 mr-1" />Resume</Button>
                  <Button size="sm" variant="outline" onClick={handleStop} className="rounded-lg border-[#223139] bg-transparent text-white"><Square className="h-3.5 w-3.5 mr-1" />Stop</Button>
                </div>
              )}
            </div>
            {transcript && (
              <div className="mt-5 rounded-xl border border-[#223139] bg-[#07131A]/60 p-3 max-h-32 overflow-y-auto thin-scroll">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#4E5F68] mb-1">Transcript</div>
                <p className="text-xs text-white/80 leading-relaxed">{transcript}</p>
              </div>
            )}
          </SurfaceCard>

          {/* Investigation — categorized photo & radiograph uploads */}
          <SurfaceCard className="p-5" data-testid="investigation-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><ImageIcon className="h-4 w-4 text-[#00A85A]" /><h3 className="text-sm font-bold text-[#101516] dark:text-white">Investigation — Photos & Radiographs</h3></div>
              <input ref={fileRef} type="file" accept="image/*,.pdf,.dcm" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImaging(f, pendingType.current || "Image"); e.target.value = ""; }} />
            </div>
            <div className="mb-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698] mb-1.5">Patient Photographs</div>
              <div className="flex flex-wrap gap-2">
                {["Intraoral", "Occlusal", "Buccal", "Smile"].map((t) => (
                  <button key={t} onClick={() => pickType(t)} data-testid={`upload-photo-${t.toLowerCase()}`} className="rounded-md border border-[#E2E8F0] dark:border-[#223139] px-2.5 py-1 text-[10px] font-semibold text-[#687173] dark:text-[#8B9698] hover:border-[#00A85A]/40 hover:text-[#00A85A]">+ {t}</button>
                ))}
              </div>
            </div>
            <div className="mb-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698] mb-1.5">Radiographs / Imaging</div>
              <div className="flex flex-wrap gap-2">
                {["IOPA", "OPG", "CBCT", "Screenshot", "PDF Report"].map((t) => (
                  <button key={t} onClick={() => pickType(t)} data-testid={`upload-radiograph-${t.toLowerCase().replace(/[^a-z]/g, "-")}`} className="rounded-md border border-[#E2E8F0] dark:border-[#223139] px-2.5 py-1 text-[10px] font-semibold text-[#687173] dark:text-[#8B9698] hover:border-[#00A85A]/40 hover:text-[#00A85A]">+ {t}</button>
                ))}
              </div>
            </div>
            {imaging.length === 0 ? <p className="text-xs text-[#8B9698]">No uploads yet. AI reads uploaded radiographs/photos; every finding stays pending until you accept, edit or reject it. Only accepted findings enter the case.</p> :
              <ImagingReview images={imaging} onUpdate={setImaging} />}
          </SurfaceCard>
        </div>

        {/* Structured findings column */}
        <div className="lg:col-span-3 space-y-6">
          {formQuestions.length > 0 && (
            <SurfaceCard className="p-5" data-testid="voice-form-answers">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-[#101516] dark:text-white">{c.condition_name || "Condition"} form <span className="text-[11px] font-normal text-[#8B9698]">· filled from voice, editable</span></h3>
                <span className="text-[11px] font-semibold text-[#00A85A]" data-testid="voice-form-filled-count">{formQuestions.filter((q) => c.form_answers?.[q.id] != null && c.form_answers[q.id] !== "").length}/{formQuestions.length} filled</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {formQuestions.map((q) => {
                  const v = c.form_answers?.[q.id] ?? "";
                  const set = (val) => setC({ ...c, form_answers: { ...(c.form_answers || {}), [q.id]: val } });
                  const commit = () => save({ form_answers: cRef.current.form_answers });
                  return (
                    <div key={q.id} className={q.type === "long_text" ? "sm:col-span-2" : ""}>
                      <Label className="text-xs font-semibold">{q.label}{v !== "" && <span className="ml-1.5 rounded bg-[#00A85A]/10 px-1.5 py-0.5 text-[9px] font-bold text-[#008F4D]">VOICE</span>}</Label>
                      {["single_choice", "dropdown", "yes_no"].includes(q.type)
                        ? <Select value={String(v)} onValueChange={(val) => save({ form_answers: { ...(cRef.current.form_answers || {}), [q.id]: val } })}><SelectTrigger className="mt-1" data-testid={`vf-${q.id}`}><SelectValue placeholder="Not captured" /></SelectTrigger><SelectContent>{(q.type === "yes_no" ? ["Yes", "No"] : q.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                        : <Input value={v} onChange={(e) => set(e.target.value)} onBlur={commit} className="mt-1" placeholder="Not captured" data-testid={`vf-${q.id}`} />}
                    </div>
                  );
                })}
              </div>
            </SurfaceCard>
          )}
          <SurfaceCard className="p-5">
            <h3 className="text-sm font-bold text-[#101516] dark:text-white mb-4">Clinical Findings <span className="text-[11px] font-normal text-[#8B9698]">· editable</span></h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <Label className="text-xs font-semibold">Chief complaint</Label>
                <Input value={c.chief_complaint || ""} onChange={(e) => setC({ ...c, chief_complaint: e.target.value })} onBlur={() => save({})} className="mt-1" placeholder="Not provided" data-testid="cc-input" />
              </div>
              {isImplantCase && <><div>
                <Label className="text-xs font-semibold">Edentulous status</Label>
                <Select value={c.arch_status || ""} onValueChange={(v) => save({ arch_status: v })}>
                  <SelectTrigger className="mt-1" data-testid="arch-status-select"><SelectValue placeholder="Not assessed" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="complete_edentulous">Complete Edentulous</SelectItem>
                    <SelectItem value="partially_edentulous">Partially Edentulous</SelectItem>
                    <SelectItem value="dentate">Dentate / No edentulous area</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Arch</Label>
                <Select value={c.arch_side || ""} onValueChange={(v) => save({ arch_side: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Not assessed" /></SelectTrigger>
                  <SelectContent><SelectItem value="maxilla">Maxilla</SelectItem><SelectItem value="mandible">Mandible</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Soft-tissue phenotype</Label>
                <Select value={c.soft_tissue || ""} onValueChange={(v) => save({ soft_tissue: v })}>
                  <SelectTrigger className="mt-1" data-testid="soft-tissue-select"><SelectValue placeholder="Not assessed" /></SelectTrigger>
                  <SelectContent><SelectItem value="thick">Thick</SelectItem><SelectItem value="thin">Thin</SelectItem><SelectItem value="unknown">Unknown / Not assessed</SelectItem></SelectContent>
                </Select>
              </div></>}
            </div>

            {/* FDI chart */}
            <div className="rounded-xl border border-[#E2E8F0] dark:border-[#223139] p-4 mb-5">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#8B9698] mb-3">FDI Tooth Chart <span className="font-normal normal-case">· tap a tooth to cycle status</span></div>
              <FdiChart findings={c.tooth_findings || []} onToggle={toggleTooth} />
            </div>

            {/* Low confidence confirmations */}
            {(c.tooth_findings || []).filter((f) => f.confidence != null && f.confidence < 80).map((f) => (
              <div key={f.fdi} className="mb-2 flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span className="text-xs text-amber-700 dark:text-amber-300 flex-1">Did you say tooth <b>{f.fdi}</b> is <b>{f.status}</b>? ({f.confidence}% confidence)</span>
                <Button size="sm" variant="outline" className="h-7 rounded-md" onClick={() => save({ tooth_findings: c.tooth_findings.map((x) => x.fdi === f.fdi ? { ...x, confidence: 100 } : x) })}><Check className="h-3 w-3" /></Button>
              </div>
            ))}
          </SurfaceCard>

          {/* Site table */}
          <SurfaceCard className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-[#101516] dark:text-white">Site-wise Assessment</h3>
              <Button size="sm" variant="outline" onClick={addSite} data-testid="add-site-btn" className="rounded-lg border-[#E2E8F0] dark:border-[#223139]"><Plus className="h-3.5 w-3.5 mr-1" />Add Site</Button>
            </div>
            {(c.sites || []).length === 0 ? <p className="text-xs text-[#8B9698]">No sites captured yet.</p> :
              <div className="space-y-2 overflow-x-auto">
                <div className="hidden sm:grid grid-cols-[70px_1fr_60px_60px_70px_80px_32px] gap-2 text-[10px] font-bold uppercase tracking-wider text-[#8B9698] px-1">
                  <span>Site</span><span>Status</span><span>MD</span><span>BL</span><span>Vertical</span><span>Tissue</span><span></span>
                </div>
                {(c.sites || []).map((s, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-[70px_1fr_60px_60px_70px_80px_32px] gap-2 items-center">
                    <Input value={s.site || ""} onChange={(e) => updateSite(i, "site", e.target.value)} onBlur={() => save({})} placeholder="12" className="h-9" data-testid={`site-fdi-${i}`} />
                    <Input value={s.status || ""} onChange={(e) => updateSite(i, "status", e.target.value)} onBlur={() => save({})} placeholder="Missing" className="h-9" />
                    <Input value={s.md || ""} onChange={(e) => updateSite(i, "md", e.target.value)} onBlur={() => save({})} placeholder="—" className="h-9" />
                    <Input value={s.bl || ""} onChange={(e) => updateSite(i, "bl", e.target.value)} onBlur={() => save({})} placeholder="—" className="h-9" />
                    <Input value={s.vertical || ""} onChange={(e) => updateSite(i, "vertical", e.target.value)} onBlur={() => save({})} placeholder="—" className="h-9" />
                    <Input value={s.tissue || ""} onChange={(e) => updateSite(i, "tissue", e.target.value)} onBlur={() => save({})} placeholder="—" className="h-9" />
                    <button onClick={() => removeSite(i)} className="text-[#8B9698] hover:text-red-500 flex justify-center"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>}
          </SurfaceCard>

          {/* Summary + generate */}
          <SurfaceCard className="p-5">
            <h3 className="text-sm font-bold text-[#101516] dark:text-white mb-1">Here's what CONTAHA understood</h3>
            <p className="text-xs text-[#8B9698] mb-4">Review and correct anything above, then generate the treatment plan.</p>
            <Button onClick={generatePlan} disabled={generating || !hasData || pendingFindings > 0} data-testid="analyze-case-btn" className="w-full h-12 bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg font-bold text-[15px]">
              {generating ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Analyzing case…</> : <><Sparkles className="h-5 w-5 mr-2" />Analyze Case & Generate Treatment Plan</>}
            </Button>
            {pendingFindings > 0 && <p className="text-[11px] text-amber-600 text-center mt-2" data-testid="pending-findings-note">{pendingFindings} AI imaging finding{pendingFindings === 1 ? "" : "s"} await your review (accept / edit / reject) before analysis.</p>}
            {!hasData && <p className="text-[11px] text-[#8B9698] text-center mt-2">Record a consultation or add findings to enable analysis.</p>}
            <p className="text-[10px] text-[#8B9698] text-center mt-3">AI assists. Clinician decides. AI never fabricates measurements, imaging findings or diagnoses.</p>
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}

function mergeByKey(existing = [], incoming = [], key) {
  if (!incoming || !incoming.length) return existing || [];
  const map = {};
  (existing || []).forEach((e) => { map[String(e[key])] = e; });
  incoming.forEach((n) => { if (n[key] != null) map[String(n[key])] = { ...map[String(n[key])], ...n }; });
  return Object.values(map);
}
